<template>
  <div class="card">
    <div v-if="!isLoading" class="card__wrapper">
      <div class="card__icon-wrapper">
        <div class="card__icon">
          <GitHubIcon v-if="labType === 'github'" />
          <AzureIcon v-if="labType === 'azure'" />
        </div>
      </div>
      <div class="card__text">
        <p>This link will launch another browser tab.</p>
        <p v-if="statusUnstarted && !isMovingToStarted">
          <span
            class="btn__link card__text__mark-started"
            @click="markAsStarted"
            >Mark as started</span
          >
        </p>
      </div>
      <div class="card__actions">
        <div
          v-if="!statusUnstarted || isMovingToStarted"
          class="card__actions__switcher"
        >
          <span
            class="card__actions__switcher__text"
            :class="{
              'card__actions__switcher__text--disabled':
                isChangingState || isMovingToStarted || isDelayed,
            }"
          >
            {{ switcherText }}
          </span>
          <SwitchCheckbox
            v-model="isLabFinished"
            :disabled="isChangingState || isMovingToStarted || isDelayed"
          />
        </div>
        <a
          class="btn btn--secondary card__cta"
          :href="lab.external_url"
          target="_blank"
          @click="changeLabToStarted"
        >
          {{ buttonText }}
        </a>
      </div>
    </div>
    <div v-else class="github-card">
      <ActionLoader
        name="ball-clip-rotate"
        loaderClass="card__loading-spinner"
      />
    </div>
  </div>
</template>
<script>
import { format } from 'date-fns'
import GitHubIcon from '@/assets/icons/course/lab/github-icon.svg'
import AzureIcon from '@/assets/icons/course/lab/azure-icon.svg'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import CONSTANTS from '@/constants'
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import { getDarklyFlagKeyValue } from '@/utils'

export default {
  components: {
    GitHubIcon: { ...GitHubIcon, name: 'GitHubIcon' },
    AzureIcon: { ...AzureIcon, name: 'AzureIcon' },
    ActionLoader,
    SwitchCheckbox,
  },
  props: {
    lab: {
      type: Object,
      default: () => {},
    },
    setLabStatus: {
      type: Function,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      isChangingState: false,
      isMovingToStarted: false,
      isDelayed: false,
    }
  },
  computed: {
    isLabFinished: {
      get() {
        return this.lab.user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      },
      set() {
        this.toggleLabStatus()
      },
    },
    statusUnstarted() {
      return this.lab.user_status === CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
    },
    labFinishedTime() {
      return format(
        new Date(this.lab.user_status_modified),
        'LLLL d, yyyy hh:mm a'
      )
    },
    labType() {
      return this.lab.lab_type || ''
    },
    cardTitle() {
      let title = ''
      switch (this.labType) {
        case 'github':
          title = 'Open Project'
          break
        case 'azure':
          if (this.isLabFinished) {
            title = 'Open Task'
          } else {
            title = 'Start Task'
          }
          break
        default:
          break
      }
      return title
    },
    buttonText() {
      let text = ''
      switch (this.labType) {
        case 'github':
          text = 'Open Repo'
          break
        case 'azure':
          text = 'Open Azure Portal'
          break
        default:
          break
      }
      return text
    },
    switcherText() {
      if (this.isLabFinished) {
        return 'Mark unfinished'
      }
      return 'Mark finished'
    },
  },
  methods: {
    async toggleLabStatus() {
      const isSetStatusDelayed = await getDarklyFlagKeyValue(
        'setContentStatusDelayActive'
      )
      if (isSetStatusDelayed) {
        this.isDelayed = true
        setTimeout(() => {
          this.isDelayed = false
        }, process.env.VUE_APP_SET_CONTENT_STATUS_DELAY)
      }

      this.isChangingState = true
      await this.setLabStatus(
        this.isLabFinished
          ? CONSTANTS.CONTENT_USER_STATUS.STARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
      this.isChangingState = false
    },
    async markAsStarted() {
      this.isMovingToStarted = true
      await this.setLabStatus(CONSTANTS.CONTENT_USER_STATUS.STARTED)
      this.isMovingToStarted = false
    },
    changeLabToStarted() {
      if (this.statusUnstarted) {
        this.setLabStatus('started')
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.card {
  width: 100%;
  &__wrapper {
    color: var(--light-blue-gray);
    min-height: 160px;
    display: flex;
    flex: 1;
    flex-wrap: nowrap;
    align-content: center;
    align-items: center;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    border-radius: 10px;
    padding: 30px;
  }
  &__text {
    text-align: left;
    padding: 30px 20px 15px;
  }
  &__icon-wrapper {
    width: 96px;
    height: 96px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 6px;
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
  }
  &__icon {
    fill: var(--white);
    transform: scale(2);
    line-height: 0;
  }
  &__actions {
    margin-left: auto;
    &__switcher {
      display: flex;
      align-items: center;
      margin-bottom: 30px;
      justify-content: flex-end;
      span {
        margin-right: 10px;
        display: inline-block;
      }
      &__text {
        color: var(--white);
        text-transform: uppercase;
        line-height: 0;
        transition: 0.5s color ease;
        &--disabled {
          color: var(--light-blue-gray);
        }
      }
    }
  }
}
</style>
